// AddPaymentModal.tsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useForm, FormProvider, useWatch, useFieldArray } from "react-hook-form";
import {
    EntityType,
    PaidFlowTypesEnum,
    PayeeAccountTypesEnum,
    PaymentService,
    PropertyService,
    TenantService,
    UnitService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { UserLoginData } from "../../components/common/charts/Enums";
import InputField from "../form-fields/InputField";
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import SelectField from "../form-fields/SelectField";
import { MAX_PAYMENT_ROWS, uploadPaymentReferenceDocument } from "./paymentDocumentHelpers";
import {
    buildPayablePayeeTypeOptions,
    buildPayeeAccountsFilterForType,
} from "./payeePaymentTypeHelpers";
import SimpleButton from "../buttons/Simple-button";
import DocumentUploader from "../CustomUploader/DocumentUploader";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import SimpleOutlineButton from "../buttons/Simple-Outline-button";
import RightSideModal from "../Modal/right-side-modal";
import { GetImages } from "../../utils/GetImages";
import { utcDateFormat } from "../../utils/helper";
import { Accordion, AccordionSection } from "../card/accordion";
import "./AddPaymentModal.css";
import CurrencyInput from "../form-fields/CurrencyInput";
import UtilHelper from "../../utils/UtilHelper";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";

// Simple Chevron Icon
const ChevronDownIcon = ({ className = "" }: { className?: string }) => (
    <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M11 1L6 6L1 1" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// --- Types ---
type PaymentItem = {
    PaymentType: string;
    Amount: string;
    DueDate: string;
    GracePeriod: string;
    Description: string;
};

type FormValues = {
    paymentMode?: string;
    PayeeTypeId?: string;
    PayeeId?: string;
    tenantId?: string;
    AttachPaymentId?: string;
    PropertyId?: string;
    UnitId?: string;
    documentfile?: File;
    accountNumber?: string;
    InvoiceNo?: string;
    payments: PaymentItem[];
};

interface AccordionState {
    basicDetailsOpen: boolean;
    paymentInfoOpen: boolean;
    basicDetailsComplete: boolean;
}

interface ApiData {
    payeeAccounts: any[];
    tenants: any[];
    properties: any[];
    units: any[];
    paymentTypes: any[];
    accountPayeeTypes: any[];
}

const initialApiData: ApiData = {
    payeeAccounts: [],
    tenants: [],
    properties: [],
    units: [],
    paymentTypes: [],
    accountPayeeTypes: [],
};

const accountOptions = [
    { Id: "Organization", Name: "Organization" },
    { Id: "Property", Name: "Property" },
    { Id: "Unit", Name: "Unit" },
];

interface AddPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    fetchData?: () => void;
    entityIdProp?: number;
    entityTypeIdProp?: EntityType;
    defaultPaymentMode?: "1" | "2";
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
    isOpen,
    onClose,
    fetchData,
    entityIdProp,
    entityTypeIdProp,
    defaultPaymentMode
}) => {
    const currencySymbol = useCurrencySymbol();
    // --- State ---
    const [resetCounter, setResetCounter] = useState(0);
    const [data, setData] = useState<ApiData>(initialApiData);
    const [isLoading, setIsLoading] = useState(false);
    const [activePaymentIndex, setActivePaymentIndex] = useState<number | null>(0);

    const [accordionState, setAccordionState] = useState<AccordionState>({
        basicDetailsOpen: true,
        paymentInfoOpen: false,
        basicDetailsComplete: false,
    });

    const organization_id = Number(sessionStorage.getItem(UserLoginData.organization_id));
    const isTenantEntity = entityTypeIdProp === EntityType.Tenant;

    // --- Form Setup ---
    const methods = useForm<FormValues>({
        defaultValues: {
            paymentMode: defaultPaymentMode || (isTenantEntity ? "2" : "1"),
            PayeeTypeId: "",
            PayeeId: "",
            tenantId: isTenantEntity ? entityIdProp?.toString() || "" : "",
            AttachPaymentId: "",
            PropertyId: !isTenantEntity && entityIdProp ? entityIdProp.toString() : "",
            UnitId: "",
            documentfile: null,
            accountNumber: "",
            InvoiceNo: "",
            payments: [{
                PaymentType: "",
                Amount: "",
                DueDate: "",
                GracePeriod: "",
                Description: "",
            }],
        },
        mode: "onChange",
    });

    const { handleSubmit, control, trigger, reset, setValue, formState: { errors, isDirty, isValid } } = methods;
    const { fields, append, remove } = useFieldArray({ control, name: "payments" });

    const watchedPayments = useWatch({ control, name: "payments" });
    const paymentMode = useWatch({ control, name: "paymentMode" });
    const PayeeTypeId = useWatch({ control, name: "PayeeTypeId" });
    const PayeeId = useWatch({ control, name: "PayeeId" });
    const AttachPaymentId = useWatch({ control, name: "AttachPaymentId" });

    const watchedPropertyId = useWatch({ control, name: "PropertyId" });
    const watchedUnitId = useWatch({ control, name: "UnitId" });
    const watchedTenantId = useWatch({ control, name: "tenantId" });

    // --- Effects ---
    useEffect(() => {
        if (isOpen) {
            reset({
                paymentMode: defaultPaymentMode || (isTenantEntity ? "2" : "1"),
                PayeeTypeId: "",
                PayeeId: "",
                tenantId: isTenantEntity ? entityIdProp?.toString() || "" : "",
                AttachPaymentId: "",
                PropertyId: (!isTenantEntity && entityTypeIdProp === EntityType.Property && entityIdProp) ? entityIdProp.toString() : "",
                UnitId: (!isTenantEntity && entityTypeIdProp === EntityType.Unit && entityIdProp) ? entityIdProp.toString() : "",
                documentfile: null,
                accountNumber: "",
                InvoiceNo: "",
                payments: [{
                    PaymentType: "",
                    Amount: "",
                    DueDate: "",
                    GracePeriod: "",
                    Description: "",
                }],
            });

            setAccordionState({
                basicDetailsOpen: true,
                paymentInfoOpen: false,
                basicDetailsComplete: false,
            });
            setActivePaymentIndex(0);
            fetchInitialData();
        }
    }, [isOpen, defaultPaymentMode, isTenantEntity, entityIdProp, entityTypeIdProp, reset]);

    useEffect(() => {
        fetchPaymentTypes();
    }, [paymentMode, PayeeId]);

    // --- API Calls ---
    const fetchInitialData = useCallback(async () => {
        try {
            const [payeeAccounts, tenants, properties, units, accountPayeeTypes] = await Promise.all([
                apiHelper(PaymentService.getApiPayeeAccountOrganizationlist, { showNotification: false }, organization_id, undefined, undefined, undefined, undefined, undefined, buildPayeeAccountsFilterForType(undefined)),
                apiHelper(TenantService.getApiTenantOrganizationlookup, { showNotification: false }, organization_id),
                apiHelper(PropertyService.getApiPropertyOrganizationlookup, { showNotification: false }, organization_id),
                apiHelper(UnitService.getApiUnitOrganizationlookup, { showNotification: false }, organization_id),
                apiHelper(PaymentService.getApiPayeeAccountPayeeTypes, { showNotification: false }),
            ]);

            setData((prev) => ({
                ...prev,
                payeeAccounts: payeeAccounts?.data?.success || [],
                tenants: tenants?.data?.success || [],
                properties: properties?.data?.success || [],
                units: units?.data?.success || [],
                accountPayeeTypes: accountPayeeTypes?.data?.success || [],
            }));
        } catch (error) {
            console.error(error);
        }
    }, [organization_id]);

    const fetchPayeeAccountsForType = useCallback(async (payeeTypeId?: string) => {
        try {
            const res = await apiHelper(
                PaymentService.getApiPayeeAccountOrganizationlist,
                { showNotification: false },
                organization_id,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                buildPayeeAccountsFilterForType(payeeTypeId)
            );

            setData((prev) => ({
                ...prev,
                payeeAccounts: res?.data?.success || [],
            }));
        } catch (error) {
            PubSub.publish(TopicsEnum.Notification.toString(), {
                message: "Failed to load payees",
                type: NotificationType.Failure,
            });
        }
    }, [organization_id]);

    useEffect(() => {
        if (paymentMode === "1") {
            fetchPayeeAccountsForType(PayeeTypeId);
        }
    }, [paymentMode, PayeeTypeId, fetchPayeeAccountsForType]);

    const handlePayeeTypeChange = useCallback(() => {
        // Payee Type cascades into Select Payee (list refetched via the effect above) -- the
        // previously selected payee belonged to the prior type's list, so clear it to avoid a
        // stale, no-longer-listed selection lingering.
        setValue("PayeeId", "", { shouldValidate: false });
    }, [setValue]);

    const fetchPaymentTypes = useCallback(async () => {
        try {
            const payeeAccountTypeId = paymentMode === "2"
                ? PayeeAccountTypesEnum.Tenant
                : paymentMode === "1"
                    ? data.payeeAccounts.find((x) => x.id === Number(PayeeId))?.payeeAccountTypeId
                    : "";

            if (payeeAccountTypeId) {
                const res = await apiHelper(PaymentService.getApiPayeeAccountPaymentTypes, { showNotification: false }, payeeAccountTypeId);
                if (res?.data?.success) {
                    setData((prev) => ({
                        ...prev,
                        paymentTypes: Array.from(new Map(res.data.success.map((item: any) => [item.id, item])).values()) || [],
                    }));
                }
            }
        } catch (error) {
            console.error(error);
        }
    }, [paymentMode, PayeeId, data.payeeAccounts]);

    // Helper to get fields that need validation
    const getFieldsToValidate = useCallback(() => {
        const fieldsToValidate: Array<keyof FormValues> = ["paymentMode", "accountNumber", "InvoiceNo"];

        if (paymentMode === "1") {
            fieldsToValidate.push("PayeeTypeId");
            fieldsToValidate.push("PayeeId");
            // Only validate AttachPaymentId if not coming from existing entity
            if (AttachPaymentId === "Property" && !entityIdProp) fieldsToValidate.push("PropertyId");
            if (AttachPaymentId === "Unit" && !entityIdProp) fieldsToValidate.push("UnitId");
        } else if (paymentMode === "2") {
            fieldsToValidate.push("tenantId");
        }
        return fieldsToValidate;
    }, [paymentMode, AttachPaymentId, entityIdProp]);

    // --- Handlers ---
    const toggleAccordion = useCallback(async (section: keyof AccordionState) => {
        if (section === "paymentInfoOpen") {
            if (accordionState.paymentInfoOpen) {
                setAccordionState((prev) => ({
                    ...prev,
                    paymentInfoOpen: false
                }));
                return;
            }

            const fields = getFieldsToValidate();
            const isValid = await trigger(fields);

            if (!isValid) {
                PubSub.publish(TopicsEnum.Notification.toString(), {
                    message: "Please fill all required basic details first",
                    type: NotificationType.Failure,
                });
                return;
            }

            setAccordionState((prev) => ({
                ...prev,
                basicDetailsComplete: true,
                paymentInfoOpen: true,
                basicDetailsOpen: false, // Optional: Close first one
            }));
        } else {
            setAccordionState((prev) => ({
                ...prev,
                basicDetailsOpen: section === "basicDetailsOpen" ? !prev.basicDetailsOpen : false,
            }));
        }
    }, [accordionState.paymentInfoOpen, getFieldsToValidate, trigger]);

    const handleNextClick = useCallback(async () => {
        const fieldsToValidate = getFieldsToValidate();

        const isValid = await trigger(fieldsToValidate);
        if (isValid) {
            setAccordionState({
                basicDetailsOpen: false,
                paymentInfoOpen: true,
                basicDetailsComplete: true,
            });
        } else {
            PubSub.publish(TopicsEnum.Notification.toString(), {
                message: "Please fill all required fields",
                type: NotificationType.Failure,
            });
        }
    }, [getFieldsToValidate, trigger]);

    const onSubmit = useCallback(async (formData: FormValues) => {
        if (isLoading) return;
        if (Object.keys(errors).length > 0 || formData.payments.length === 0) return;
        setIsLoading(true);
        try {
            const processPayments = async (paymentConfig: { entityId: number; entityTypeId: EntityType; paidFlowTypeId: PaidFlowTypesEnum; isOrganizationAccount?: boolean; }) => {
                let payeeAccountId = 0;
                await Promise.all(
                    formData.payments.map(async (item, index: any) => {
                        payeeAccountId = Number(formData.PayeeId) || 0;
                        if (paymentConfig.isOrganizationAccount) {
                            const response = await apiHelper(PaymentService.getApiPayeeAccountOrganizationlist, { showNotification: false }, organization_id, undefined, undefined, undefined, undefined, undefined, `IsOrganizationAccount==true`);
                            payeeAccountId = response?.data?.success?.[index]?.id || payeeAccountId;
                        }

                        const paymentObj = {
                            amount: Number(item.Amount.toString().replace(/,/g, "")),
                            description: item.Description,
                            dueDate: paymentConfig.paidFlowTypeId === PaidFlowTypesEnum.Receiveable ? utcDateFormat(item.DueDate) : new Date(item.DueDate).toISOString(),
                            entityId: paymentConfig.entityId,
                            entityTypeId: paymentConfig.entityTypeId,
                            payeeAccountId,
                            gracePeriodInDays: Number(item.GracePeriod) || 0,
                            paymentTypeId: Number(item.PaymentType) || 0,
                            paidFlowTypeId: paymentConfig.paidFlowTypeId,
                            accountNumber: formData.accountNumber || "",
                            referenceNumber: formData.InvoiceNo,
                        };

                        const paymentResponse = await apiHelper(PaymentService.postApiPaymentDueAccount, {
                            title: "Payment Added", successMessage: "Payment added successfully", successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />, showNotification: true
                        }, paymentObj);

                        if (formData.documentfile && paymentResponse?.data?.success) {
                            await uploadPaymentReferenceDocument(
                                paymentResponse.data.success.id,
                                formData.documentfile,
                            );
                        }
                    }));

                if (fetchData) fetchData();
                onClose();
            };

            if (formData.paymentMode === "1") {
                const { entityId, entityTypeId } = (() => {
                    if (entityIdProp && entityTypeIdProp) return { entityId: Number(entityIdProp), entityTypeId: entityTypeIdProp };
                    if (formData.PropertyId && Number(formData.PropertyId) > 0) return { entityId: Number(formData.PropertyId), entityTypeId: EntityType.Property };
                    if (formData.UnitId && Number(formData.UnitId) > 0) return { entityId: Number(formData.UnitId), entityTypeId: EntityType.Unit };
                    return { entityId: Number(organization_id), entityTypeId: EntityType.Organization };
                })();
                await processPayments({ entityId, entityTypeId, paidFlowTypeId: PaidFlowTypesEnum.Payable });
            } else if (formData.paymentMode === "2") {
                if (!formData.tenantId) throw new Error("Tenant ID required");
                await processPayments({ entityId: Number(formData.tenantId), entityTypeId: EntityType.Tenant, paidFlowTypeId: PaidFlowTypesEnum.Receiveable, isOrganizationAccount: true });
            }
        } catch (error: any) {
            PubSub.publish(TopicsEnum.Notification.toString(), { message: error.message || "Failed to submit", type: NotificationType.Failure });
        } finally {
            setIsLoading(false);
        }
    }, [organization_id, fetchData, onClose, entityIdProp, entityTypeIdProp, errors]);

    // Validate the whole form first (the same path the real Save uses -- handleSubmit is
    // what flips formState.isSubmitted, which is what every field's error-message display
    // actually gates on) so a half-filled row can't be left behind by adding another blank one.
    // handleSubmit's validation is async, so a second click landing before the first
    // resolves would otherwise both see the pre-append state as valid and both append --
    // isAddingRowRef closes that re-entrancy window.
    const isAddingRowRef = useRef(false);
    const addPaymentRow = useCallback(() => {
        if (fields.length >= MAX_PAYMENT_ROWS || isAddingRowRef.current) return;
        isAddingRowRef.current = true;
        handleSubmit(
            () => {
                append({ PaymentType: "", Amount: "", DueDate: "", GracePeriod: "", Description: "" });
                setActivePaymentIndex(fields.length);
                isAddingRowRef.current = false;
            },
            () => {
                // Invalid -- do not append. handleSubmit already set isSubmitted, surfacing
                // every field's existing validation message.
                isAddingRowRef.current = false;
            }
        )();
    }, [append, fields.length, handleSubmit]);

    const handleRemoveItem = (index: number) => {
        remove(index);
        if (activePaymentIndex === index) {
            setActivePaymentIndex(null);
        } else if (activePaymentIndex !== null && activePaymentIndex > index) {
            setActivePaymentIndex(activePaymentIndex - 1);
        }
    };

    // --- Options ---
    const payeeAccountsOptions = useMemo(() => data.payeeAccounts.map((t: any) => ({ Id: t.id, Name: t.accountName })), [data.payeeAccounts]);
    const tenantOptions = useMemo(() => data.tenants.map((t: any) => ({ Id: t.id, Name: t.name })), [data.tenants]);
    const propertyOptions = useMemo(() => data.properties.map((t: any) => ({ Id: t.id, Name: t.name })), [data.properties]);
    const unitOptions = useMemo(() => data.units.map((t: any) => ({ Id: t.id, Name: t.name })), [data.units]);
    const paymentTypeOptions = useMemo(() => data.paymentTypes.map((t: any) => ({ Id: t.id, Name: t.name })), [data.paymentTypes]);
    const accountPayeeTypesOptions = useMemo(() => data.accountPayeeTypes.map((t: any) => ({ Id: t.id, Name: t.name })), [data.accountPayeeTypes]);
    const payablePayeeTypeOptions = useMemo(() => buildPayablePayeeTypeOptions(accountPayeeTypesOptions), [accountPayeeTypesOptions]);

    const getPaymentTypeName = (id?: string) => {
        if (!id) return "Select Type";
        return paymentTypeOptions.find((p) => p.Id.toString() === id.toString())?.Name || "Select Type";
    };

    // --- Helper to get the Name of the entity based on ID ---
    const getEntityName = () => {
        if (paymentMode === "2") {
            // Tenant Mode
            return tenantOptions.find((x) => x.Id.toString() === watchedTenantId)?.Name || "-";
        }

        // Payables Mode
        if (entityIdProp && entityTypeIdProp) {
            if (entityTypeIdProp === EntityType.Property) return propertyOptions.find(x => x.Id.toString() === entityIdProp.toString())?.Name || "-";
            if (entityTypeIdProp === EntityType.Unit) return unitOptions.find(x => x.Id.toString() === entityIdProp.toString())?.Name || "-";
            return "Organization";
        }

        // Fallback for dynamic selection
        if (AttachPaymentId === "Property") return propertyOptions.find((x) => x.Id.toString() === watchedPropertyId)?.Name || "-";
        if (AttachPaymentId === "Unit") return unitOptions.find((x) => x.Id.toString() === watchedUnitId)?.Name || "-";
        return "-";
    };

    // --- Helper to get Label ---
    const getEntityLabel = () => {
        if (paymentMode === "2") return "Tenant";
        if (entityIdProp) {
            return entityTypeIdProp === EntityType.Property ? "Property" : entityTypeIdProp === EntityType.Unit ? "Unit" : "Organization";
        }
        return AttachPaymentId === "Property" ? "Property" : AttachPaymentId === "Unit" ? "Unit" : "Entity";
    };

    return (
        <RightSideModal
            isOpen={isOpen}
            onClose={onClose}
            title="Add Payment"
            buttonlabel="Save"
            formId="AddPaymentModalForm"
            maxWidth={600}
            className="transparent-bg"
            isDirty={isDirty}
            isSubmitDisabled={isLoading || !isDirty || !isValid}
            isLoading={isLoading}
        >
            <FormProvider {...methods}>
                <form id="AddPaymentModalForm" className="paymentModalForm" onSubmit={handleSubmit(onSubmit)}>

                    <Accordion className="style-2">
                        {/* --- Section 1: Basic Details --- */}
                        <AccordionSection
                            title="Basic Details"
                            isOpen={accordionState.basicDetailsOpen}
                            onToggle={() => toggleAccordion("basicDetailsOpen")}
                        >
                            <div className="accordion-body">
                                <div className="row">

                                    {/* Left Column: Payment Type Static Text */}
                                    <div className="col-6 mb-3">
                                        <label className="static-field-label">Payment Type</label>
                                        <div className="static-field-value">
                                            {paymentMode === "1" ? "Payables" : "Receivables"}
                                        </div>
                                    </div>

                                    {/* Right Column: Entity Name Static Text */}
                                    <div className="col-6 mb-3">
                                        <label className="static-field-label">{getEntityLabel()}</label>
                                        <div className="static-field-value">
                                            {getEntityName()}
                                        </div>
                                    </div>

                                    {/* Payee Type + Payee Select (cascading, matches AddPayment.tsx) */}
                                    {paymentMode === "1" && (
                                        <>
                                            <div className="col-md-6">
                                                <SelectField
                                                    placeholderText="Select Payee Type"
                                                    name="PayeeTypeId"
                                                    label="Payee Type"
                                                    required
                                                    requiredMessage="Payee Type is required"
                                                    options={payablePayeeTypeOptions}
                                                    disabled={isLoading}
                                                    key={`payee-type-${resetCounter}`}
                                                    isSearchable={true}
                                                    onChange={handlePayeeTypeChange}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <SelectField
                                                    placeholderText="Select Payee"
                                                    name="PayeeId"
                                                    label="Select Payee"
                                                    required
                                                    requiredMessage="Select Payee is required"
                                                    options={payeeAccountsOptions}
                                                    disabled={isLoading || !PayeeTypeId}
                                                    key={resetCounter}
                                                    isSearchable={true}

                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Dynamic Selects (Only if NOT coming from a specific entity page, handling legacy flow just in case) */}
                                    {!entityIdProp && paymentMode === "1" && (
                                        <>
                                            {/* Hidden select logically kept for form data, but hidden via d-none */}
                                            <div className="d-none">
                                                <SelectField name="AttachPaymentId" label="hidden" options={accountOptions} />
                                            </div>

                                            {AttachPaymentId === "Property" && (
                                                <div className="col-12 mt-2">
                                                    <SelectField placeholderText="Select Property" name="PropertyId" label="Property" required requiredMessage="Property is required" options={propertyOptions} disabled={isLoading} isSearchable={true} />
                                                </div>
                                            )}
                                            {AttachPaymentId === "Unit" && (
                                                <div className="col-12 mt-2">
                                                    <SelectField placeholderText="Select Unit" name="UnitId" label="Unit" required requiredMessage="Unit is required" options={unitOptions} disabled={isLoading} isSearchable={true} />
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Tenant Select (if manual selection needed) */}
                                    {paymentMode === "2" && !entityIdProp && (
                                        <div className="col-12">
                                            <SelectField placeholderText="Select Tenant" name="tenantId" label="Tenant" required requiredMessage="Select Tenant is required" options={tenantOptions} disabled={isLoading || isTenantEntity} isSearchable={true} />
                                        </div>
                                    )}
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <InputField
                                            name="accountNumber"
                                            label="Account Number"
                                            required
                                            requiredMessage="Account Number is required"
                                            type="text"
                                            placeholder="Enter Account Number"
                                            validation={{
                                                required: "Account number is required",
                                                minLength: {
                                                    value: 1,
                                                    message: "Account number is required",
                                                },
                                                maxLength: {
                                                    value: 50,
                                                    message: "Account number cannot exceed 50 characters",
                                                },
                                            }}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <InputField
                                            name="InvoiceNo"
                                            label="Invoice No./Bill No."
                                            type="text"
                                            placeholder="Enter Invoice No./Bill No."
                                            required
                                            requiredMessage="Invoice No./Bill No. is required"
                                            validation={{
                                                required: "Invoice No./Bill No. is required",
                                                minLength: {
                                                    value: 1,
                                                    message: "Invoice No./Bill No. is required",
                                                },
                                                maxLength: {    
                                                    value: 100,
                                                    message: "Invoice No./Bill No. cannot exceed 100 characters",
                                                },
                                            }}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="fw-600 black">Upload Bill / Invoice <span className="optionalLabel">(optional)</span> </label>
                                            <DocumentUploader name="documentfile" maxSizeMB={5} resetTrigger={resetCounter} />
                                        </div>
                                    </div>
                                </div>

                                {/* Next Button inside Accordion */}
                                <div className="next-btn-wrapper">
                                    <SimpleButton type="button" label="Next" className="next-btn" onClick={handleNextClick} />
                                </div>
                            </div>
                        </AccordionSection>

                        {/* --- Section 2: Payment Information --- */}
                        <AccordionSection
                            title="Payment Information"
                            isDisabled={!accordionState.basicDetailsComplete}
                            isOpen={accordionState.paymentInfoOpen}
                            onToggle={() => toggleAccordion("paymentInfoOpen")}
                        >
                            <div className="accordion-body">

                                {fields.map((field, index) => {
                                    const isOpen = activePaymentIndex === index;
                                    const currentValues = watchedPayments?.[index];

                                    // --- LOGIC: Check specific row errors ---
                                    const paymentErrors = errors.payments?.[index];
                                    const hasError = paymentErrors && Object.keys(paymentErrors).length > 0;

                                    return (
                                        <div key={field.id} className="payment-item-card">

                                            {/* --- BODY SECTION (Inputs) --- */}
                                            <div className={`payment-item-body ${!isOpen ? "d-none" : ""}`}>
                                                <div className="payment-item-header">
                                                    <span className="payment-item-title">Payment Details</span>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {fields.length > 1 && (
                                                            <div className="action-icon-wrapper" onClick={() => handleRemoveItem(index)}>
                                                                <DeleteRedIcon fill="#E02424" width="16" height="16" />
                                                            </div>
                                                        )}
                                                        <div
                                                            role="button"
                                                            onClick={() => setActivePaymentIndex(null)}
                                                            className="chevron-wrapper open"
                                                        >
                                                            <ChevronDownIcon />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <SelectField
                                                            placeholderText="Select Type"
                                                            name={`payments.${index}.PaymentType`}
                                                            label="Type"
                                                            required
                                                            requiredMessage="Payment Type is required"
                                                            options={paymentTypeOptions}
                                                            disabled={isLoading}
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <CurrencyInput
                                                            name={`payments.${index}.Amount`}
                                                            label={`Amount (${currencySymbol})`}
                                                            required
                                                            requiredMessage="Amount is required"
                                                            placeholder="Amount"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <InputField
                                                            name={`payments.${index}.DueDate`}
                                                            label="Due Date"
                                                            type="date"
                                                            required
                                                            requiredMessage="Due date is required"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <InputField
                                                            name={`payments.${index}.GracePeriod`}
                                                            label="Grace Period"
                                                            type="number"
                                                            required
                                                            requiredMessage="Grace period is required"
                                                            placeholder="Days"
                                                            validation={{
                                                                min: { value: 0, message: "Grace period cannot be negative" },
                                                                maxLength: {
                                                                    value: 2,
                                                                    message: "Grace period cannot exceed 99 days"
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <DescriptionTextarea
                                                            name={`payments.${index}.Description`}
                                                            label="Description"
                                                            placeholder="Description"
                                                            required
                                                            requiredMessage="Description is required"
                                                            alignCounterWithError
                                                            validation={{
                                                                maxLength: {
                                                                    value: 250,
                                                                    message: "Description cannot exceed 250 characters"
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* --- SUMMARY SECTION (Collapsed View) --- */}
                                            <div
                                                onClick={() => setActivePaymentIndex(index)}
                                                className={`payment-item-summary ${isOpen ? "d-none" : ""} ${hasError ? "payment-item-summary--error" : ""}`}
                                            >
                                                <div className="summary-content">
                                                    {hasError && (
                                                        <div style={{ color: "#E02424", fontSize: "12px", width: "100%", marginBottom: "5px", fontWeight: "600" }}>
                                                            Missing Details
                                                        </div>
                                                    )}

                                                    <div className="d-flex w-100 justify-content-between">
                                                        <div className="summary-col">
                                                            <div className="summary-label">Type</div>
                                                            <div className={`summary-value ${paymentErrors?.PaymentType ? "text-danger" : ""}`}>
                                                                {getPaymentTypeName(currentValues?.PaymentType)}
                                                            </div>
                                                        </div>
                                                        <div className="summary-col">
                                                            <div className="summary-label">Due Date</div>
                                                            <div className={`summary-value ${paymentErrors?.DueDate ? "text-danger" : ""}`}>
                                                                {currentValues?.DueDate ? new Date(currentValues.DueDate).toLocaleDateString() : "-"}
                                                            </div>
                                                        </div>
                                                        <div className="summary-col summary-col--amount">
                                                            <div className="summary-label">Amount</div>
                                                            <div className={`summary-value ${paymentErrors?.Amount ? "text-danger" : ""}`}>
                                                                <CurrencyTooltip
                                                                    content={UtilHelper.CurrencyFormat(UtilHelper.parseCurrencyToNumber(currentValues?.Amount), false)}
                                                                    formatted={UtilHelper.formatCurrency(UtilHelper.parseCurrencyToNumber(currentValues?.Amount), false)}
                                                                >
                                                                    <span>{UtilHelper.formatCurrency(UtilHelper.parseCurrencyToNumber(currentValues?.Amount), false)}</span>
                                                                </CurrencyTooltip>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-3">
                                                    {fields.length > 1 && (
                                                        <div
                                                            role="button"
                                                            className="action-icon-wrapper"
                                                            onClick={(e) => { e.stopPropagation(); handleRemoveItem(index); }}
                                                        >
                                                            <DeleteRedIcon fill="#E02424" width="16" height="16" />
                                                        </div>
                                                    )}
                                                    <div className="chevron-wrapper">
                                                        <ChevronDownIcon />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="d-flex align-items-center gap-2 mt-3">
                                    <SimpleOutlineButton 
                                        type="button" 
                                        label="+ Add More" 
                                        onClick={addPaymentRow} 
                                        isDisabled={fields.length >= MAX_PAYMENT_ROWS}
                                    />
                                    {fields.length >= MAX_PAYMENT_ROWS && (
                                        <p className="text-danger f-12 mb-0">
                                            Maximum {MAX_PAYMENT_ROWS} payments allowed
                                        </p>
                                    )}
                                </div>
                            </div>
                        </AccordionSection>
                    </Accordion>
                </form>
            </FormProvider>
        </RightSideModal>
    );
};

export default AddPaymentModal;